import React from 'react'
import { connect } from 'react-redux'
import { I18n } from 'react-redux-i18n'
import { withStateHandlers } from 'recompose'

import { ConfirmModal } from './ConfirmModal'
import { ServiceTxnCancelAction } from './ServiceTxnCancelAction'
import { checkIfAnyGrantedByGivenAuth } from '../modules/authService'

const ModalShell = ({ title, onHide, children, footer }) => (
  <div>
    {/* Backdrop */}
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50"
      onClick={onHide}
    />

    {/* Modal */}
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <button
            onClick={onHide}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6">{children}</div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          {footer}
        </div>
      </div>
    </div>
  </div>
)

const CloseButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-100 transition-colors"
  >
    {I18n.t('action.close')}
  </button>
)

// Non-supervisors still see the cancel button, but are told to escalate
// rather than being able to perform the cancellation themselves.
const NotSupervisorModal = ({ onHide }) => (
  <ModalShell
    title={I18n.t('cancelServiceTxnModal.notSupervisorHeader')}
    onHide={onHide}
    footer={<CloseButton onClick={onHide} />}
  >
    <p className="text-lg text-gray-700">
      {I18n.t('cancelServiceTxnModal.notSupervisorDetail')}
    </p>
  </ModalShell>
)

const withReason = withStateHandlers(() => ({ reason: '' }), {
  onReasonChange: () => (reason) => ({ reason }),
})

const SupervisorModal = withReason(
  ({ onHide, cancelServiceTxn, onConfirmed, reason, onReasonChange }) => {
    const trimmedReason = reason.trim()
    const isValid = trimmedReason.length > 0
    return (
      <ModalShell
        title={I18n.t('cancelServiceTxnModal.header')}
        onHide={onHide}
        footer={
          <div className="flex items-center gap-3">
            <CloseButton onClick={onHide} />
            <button
              disabled={!isValid}
              onClick={() => {
                if (!isValid) return
                cancelServiceTxn(onConfirmed)(trimmedReason)
              }}
              className={
                isValid
                  ? 'px-6 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors shadow-sm'
                  : 'px-6 py-2.5 bg-red-300 text-white rounded-lg font-medium cursor-not-allowed shadow-sm'
              }
            >
              {I18n.t('action.cancel')}
            </button>
          </div>
        }
      >
        <p className="text-lg text-gray-700">
          {I18n.t('cancelServiceTxnModal.detail')}
        </p>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {I18n.t('cancelServiceTxnModal.reasonLabel')}
            <span className="text-red-600"> *</span>
          </label>
          <textarea
            autoFocus
            rows={3}
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder={I18n.t('cancelServiceTxnModal.reasonPlaceholder')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
          />
          {!isValid && (
            <p className="mt-1 text-sm text-red-600">
              {I18n.t('cancelServiceTxnModal.reasonRequired')}
            </p>
          )}
        </div>
      </ModalShell>
    )
  },
)

const ServiceTxnCancelConfirmModalView = ({
  serviceTxn,
  serviceTxnDidConfirm,
  isSupervisor,
  children,
}) => (
  <ServiceTxnCancelAction serviceTxn={serviceTxn}>
    {(cancelServiceTxn) => (
      <ConfirmModal>
        {({ isShow, showModal, onHide }) => (
          <div>
            {children(showModal)}

            {isShow &&
              (isSupervisor ? (
                <SupervisorModal
                  onHide={onHide}
                  cancelServiceTxn={cancelServiceTxn}
                  onConfirmed={() => {
                    onHide()
                    if (serviceTxnDidConfirm) serviceTxnDidConfirm()
                  }}
                />
              ) : (
                <NotSupervisorModal onHide={onHide} />
              ))}
          </div>
        )}
      </ConfirmModal>
    )}
  </ServiceTxnCancelAction>
)

export const ServiceTxnCancelConfirmModal = connect(({ auth }) => ({
  isSupervisor: checkIfAnyGrantedByGivenAuth(auth, ['supervisor']),
}))(ServiceTxnCancelConfirmModalView)
